import React, { useState, useEffect } from "react";
import TextareaAutosize from 'react-textarea-autosize';

import {
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  DragIndicator as DragIndicatorIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { NumericFormat } from 'react-number-format';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensors,
  useSensor,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useSidebarState } from '@/__core/hooks/useSidebarState';
import { calculateTableWidth } from '@/utils/tableWidth';
import { getProductTypeTax, ProductTypeTaxItem } from "@/modules/settings/redux/SettingsCRUD";
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';
import OrderItemEditModal from './OrderItemEditModal';
import DeleteConfirmationModal from '@/__core/components/DeleteConfirmationModal';
import {
  getContentTypeId,
  uploadDocument,
  getDocumentsByObject,
  deleteDocument,
  Document,
} from '@/modules/documents/redux/DocumentCRUD';

const PAPER_HORIZONTAL_PADDING = 0;

const styles = sharedFormStyles.orderItemsTableStyles;

interface OrderItem {
  id: number;
  product_id?: string;
  product_code?: string;
  product_name: string;
  item_type_tax: string;
  color: string;
  quantity: number;
  unit?: {
    id: string;
    name: string;
  } | null;
  unit_id: string;
  unit_name: string;
  unit_price: number;
  total_price_not_tax: number;
  total_price: number;
  notes?: string;
  isGroup?: boolean;
  groupName?: string;
}

interface OrderItemsTableProps {
  orderId?: string;
  orderItems: OrderItem[];
  units: Array<{ id: string; name: string; code: string }>;
  productTypes?: ProductTypeTaxItem[];
  disabled?: boolean;
  onItemChange: (itemId: number, field: keyof OrderItem, value: any) => void;
  onAddOrderItem: () => void;
  onAddGroupItem: () => void;
  onRemoveOrderItem: (itemId: number) => void;
  onReorderItems?: (newOrder: OrderItem[]) => void;
  onUploadFiles?: (itemId: number, files: File[]) => void | Promise<void>;
}

// Sortable Table Row Component
interface SortableTableRowProps {
  item: OrderItem;
  index: number;
  orderItems: OrderItem[];
  productTypes: ProductTypeTaxItem[];
  disabled?: boolean;
  orderItemContentTypeId: number | null;
  onEditItem: (item: OrderItem) => void;
  onItemChange: (itemId: number, field: keyof OrderItem, value: any) => void;
  onRemoveOrderItem: (itemId: number) => void;
  onUploadFiles?: (itemId: number, files: File[]) => void | Promise<void>;
  itemDocuments: Document[];
  isLoadingItemDocuments?: boolean;
  onReloadItemDocuments?: (itemId: number) => Promise<void> | void;
  onDeleteItemDocument?: (itemId: number, documentId: string) => Promise<void> | void;
  isDraggingRef?: React.MutableRefObject<boolean>;
}

const SortableTableRow: React.FC<SortableTableRowProps> = ({
  item,
  index,
  orderItems,
  productTypes,
  disabled = false,
  orderItemContentTypeId,
  onEditItem,
  onItemChange,
  onRemoveOrderItem,
  onUploadFiles,
  itemDocuments,
  isLoadingItemDocuments = false,
  onReloadItemDocuments,
  onDeleteItemDocument,
  isDraggingRef,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const uploadInputRef = React.useRef<HTMLInputElement | null>(null);


  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleUploadFiles = React.useCallback(async (files: FileList | null) => {
    const selectedFiles = Array.from(files || []);
    if (selectedFiles.length === 0) {
      return;
    }

    try {
      if (onUploadFiles) {
        await onUploadFiles(item.id, selectedFiles);
      } else {
        if (!orderItemContentTypeId) {
          return;
        }

        await Promise.all(selectedFiles.map(async (file) => {
          await uploadDocument({
            content_type_id: orderItemContentTypeId,
            object_id: String(item.id),
            name: file.name,
            file,
            mime_type: file.type || 'application/octet-stream',
          });
        }));
      }
    } finally {
      await onReloadItemDocuments?.(item.id);
    }
  }, [item.id, onUploadFiles, orderItemContentTypeId, onReloadItemDocuments]);

  // Calculate display index for items within their group
  const displayIndex = (() => {
    if (item.isGroup) return null;
    // Simply use the index among all non-group items (1-based)
    let itemCount = 0;
    for (let i = 0; i <= index; i++) {
      if (!orderItems[i].isGroup) {
        itemCount++;
      }
    }
    return itemCount;
  })();

  const taxName = productTypes.find((pt) => pt.id === item.item_type_tax)?.name || '';
  const unitName = item.unit?.name || item.unit_name || '';

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      className={item.isGroup ? "group-row" : ""}
      sx={styles.row(!!item.isGroup, isDragging)}
      onClick={() => {
        if (isDraggingRef?.current) return;
        onEditItem(item);
      }}
    >
      <TableCell sx={styles.dragCell}>
        <IconButton
          size="small"
          data-drag-handle="true"
          {...(disabled ? {} : { ...attributes, ...listeners })}
          disabled={disabled}
          sx={styles.dragButton(disabled)}
          onClick={(event) => event.stopPropagation()}
        >
          <DragIndicatorIcon fontSize="small" sx={styles.dragIcon} />
        </IconButton>
        <Box sx={styles.dragCellInner}>
          {!item.isGroup && (
            <Typography sx={styles.indexText}>
              {displayIndex}
            </Typography>
          )}
        </Box>
      </TableCell>

      <TableCell sx={styles.productCell} colSpan={item.isGroup ? 8 : 1}>
        <TextareaAutosize
          value={item.isGroup ? (item.groupName || '') : (item.product_name || '')}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
            if (disabled) return;
            if (item.isGroup) {
              onItemChange(item.id, "groupName", e.target.value);
            } else {
              onItemChange(item.id, "product_name", e.target.value);
            }
          }}
          placeholder={item.isGroup ? "Nhập tên phân vùng..." : "Tên sản phẩm"}
          spellCheck={false}
          autoComplete="off"
          readOnly={disabled}
          style={styles.productNameTextarea(!!item.isGroup, disabled)}
        />
      </TableCell>

      {!item.isGroup && (
        <>
          <TableCell sx={styles.colorCell}>
            <TextareaAutosize
              value={item.color || ''}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                if (disabled) return;
                onItemChange(item.id, "color", e.target.value);
              }}
              placeholder="Màu sắc"
              spellCheck={false}
              autoComplete="off"
              readOnly={disabled}
              style={styles.colorTextarea(disabled)}
            />
          </TableCell>
          <TableCell sx={styles.quantityCell}>
            <NumericFormat
              value={item.quantity}
              displayType="text"
              decimalScale={2}
              thousandSeparator="."
              decimalSeparator=","
              renderText={(value) => <Typography sx={styles.totalText}>{value}</Typography>}
            />
          </TableCell>
          <TableCell sx={{ ...styles.unitCell, width: 90, minWidth: 90, maxWidth: 90 }}>
            <Typography sx={{ fontSize: '0.875rem' }}>{unitName}</Typography>
          </TableCell>
          <TableCell sx={{ ...styles.unitPriceCell, textAlign: 'right' }}>
            <NumericFormat
              value={item.unit_price}
              displayType="text"
              decimalScale={0}
              thousandSeparator="."
              decimalSeparator=","
              renderText={(value) => <Typography sx={styles.totalText}>{value}</Typography>}
            />
          </TableCell>
          <TableCell sx={{ ...styles.totalCell, textAlign: 'right' }}>
            <NumericFormat
              value={item.total_price}
              displayType="text"
              thousandSeparator="."
              decimalSeparator=","
              decimalScale={0}
              renderText={(value) => <Typography sx={styles.totalText}>{value}</Typography>}
            />
          </TableCell>
          <TableCell sx={styles.taxCell}>
            <Typography sx={{ fontSize: '0.875rem' }}>{taxName}</Typography>
          </TableCell>
          <TableCell sx={{ ...styles.vatCell, textAlign: 'right' }}>
            <NumericFormat
              value={item.total_price_not_tax}
              displayType="text"
              thousandSeparator="."
              decimalSeparator=","
              decimalScale={0}
              renderText={(value) => <Typography sx={styles.totalText}>{value}</Typography>}
            />
          </TableCell>
          <TableCell
            sx={{
              ...styles.headActionWidthCell,
              minWidth: 150,
              width: 150,
              maxWidth: 150,
              verticalAlign: 'top',
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <Tooltip title="Upload hình ảnh hoặc file">
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <input
                  ref={(node) => {
                    uploadInputRef.current = node;
                  }}
                  type="file"
                  hidden
                  multiple
                  accept="image/*,.txt,.png,.jpg,.jpeg,.pdf,.doc,.docx,.zip,.rar,.mp4,.mp3,.avi,.mov,.gif,.bmp,.tiff,.svg,.webp"
                  onChange={(event) => {
                    if (disabled) {
                      event.target.value = '';
                      return;
                    }

                    void handleUploadFiles(event.target.files);
                    event.target.value = '';
                  }}
                />
                {itemDocuments.length === 0 && (
                  <IconButton
                    size="small"
                    disabled={disabled}
                    onClick={(event) => {
                      event.stopPropagation();
                      uploadInputRef.current?.click();
                    }}
                    sx={{
                      width: 28,
                      height: 28,
                      color: disabled ? '#9ca3af' : '#2563eb',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      backgroundColor: disabled ? '#f3f4f6' : '#eff6ff',
                      opacity: 1,
                      '&:hover': {
                        backgroundColor: disabled ? '#f3f4f6' : '#dbeafe',
                        borderColor: disabled ? '#d1d5db' : '#93c5fd',
                      },
                    }}
                  >
                    <CloudUploadIcon fontSize="small" />
                  </IconButton>
                )}

                {isLoadingItemDocuments && (
                  <Typography sx={{ fontSize: '0.7rem', color: '#6b7280' }}>
                    Đang tải file...
                  </Typography>
                )}

                {itemDocuments.length > 0 && (
                  <Box sx={{ width: '100%' }}>
                    {itemDocuments.map((doc) => (
                      <Box
                        key={doc.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          borderRadius: '6px',
                          px: 0.5,
                          py: 0.25,
                          mb: 0.25,
                          backgroundColor: 'transparent',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
                          <AttachFileIcon sx={{ fontSize: 14, color: '#6b7280' }} />
                          <a
                            href={doc.file_url || '#'}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(event) => {
                              event.stopPropagation();
                              if (!doc.file_url) {
                                event.preventDefault();
                              }
                            }}
                            style={{
                              color: '#1d4ed8',
                              fontSize: '11px',
                              textDecoration: 'none',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block',
                              maxWidth: '100%',
                            }}
                            title={doc.name}
                          >
                            {doc.name}
                          </a>
                          <IconButton
                            size="small"
                            title="Xóa file"
                            disabled={disabled}
                            onClick={(event) => {
                              event.stopPropagation();
                              if (disabled) {
                                return;
                              }
                              void onDeleteItemDocument?.(item.id, doc.id);
                            }}
                            sx={{
                              p: 0,
                              width: 18,
                              height: 18,
                              minWidth: 18,
                              borderRadius: '999px',
                              border: '1px solid #fca5a5',
                              backgroundColor: '#fef2f2',
                              color: '#dc2626',
                              fontSize: '12px',
                              fontWeight: 700,
                              opacity: disabled ? 0.5 : 1,
                              '&:hover': {
                                backgroundColor: disabled ? '#fef2f2' : '#fee2e2',
                              },
                            }}
                          >
                            x
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Tooltip>
          </TableCell>
          <TableCell onClick={(event) => event.stopPropagation()}>
            <IconButton
              size="small"
              onClick={() => {
                if (disabled) return;
                onRemoveOrderItem(item.id);
              }}
              disabled={disabled}
              className="delete-icon"
              sx={styles.deleteButton(disabled)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </TableCell>
        </>
      )}
      {item.isGroup && (
        <>
          <TableCell sx={styles.headActionWidthCell}></TableCell>
          <TableCell sx={styles.groupDeleteCell} onClick={(event) => event.stopPropagation()}>
            <IconButton
              size="small"
              onClick={() => {
                if (disabled) return;
                onRemoveOrderItem(item.id);
              }}
              disabled={disabled}
              className="delete-icon"
              sx={styles.deleteButton(disabled)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </TableCell>
        </>
      )}
    </TableRow>
  );
};

export default function OrderItemsTable({
  orderId,
  orderItems,
  units,
  productTypes: productTypesProp,
  disabled = false,
  onItemChange,
  onAddOrderItem,
  onAddGroupItem,
  onRemoveOrderItem,
  onReorderItems,
  onUploadFiles,
}: OrderItemsTableProps) {

  const [localProductTypes, setLocalProductTypes] = useState<ProductTypeTaxItem[]>([]);
  const [tableWidth, setTableWidth] = useState<number>(0);
  const [orderItemContentTypeId, setOrderItemContentTypeId] = useState<number | null>(null);
  const [itemDocumentsMap, setItemDocumentsMap] = useState<Record<string, Document[]>>({});
  const [itemDocumentsLoadingMap, setItemDocumentsLoadingMap] = useState<Record<string, boolean>>({});
  const loadedDocumentItemIdsRef = React.useRef<Set<string>>(new Set());
  const inFlightDocumentsRequestRef = React.useRef<Map<string, Promise<Document[]>>>(new Map());
  const sidebarOpen = useSidebarState();
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [deleteConfirmItem, setDeleteConfirmItem] = useState<OrderItem | null>(null);
  const tableContainerRef = React.useRef<HTMLDivElement | null>(null);
  const tableDragScrollRef = React.useRef({ active: false, moved: false, startX: 0, scrollLeft: 0 });
  const [isTableDragScrolling, setIsTableDragScrolling] = useState(false);
  const suppressTableClickRef = React.useRef(false);

  const isInteractiveTarget = React.useCallback((target: EventTarget | null): boolean => {
    const element = target as HTMLElement | null;
    if (!element || typeof element.closest !== 'function') return false;
    return Boolean(element.closest(
      'input, textarea, button, a, [role="button"], .MuiIconButton-root, .MuiCheckbox-root, .MuiChip-root'
    ));
  }, []);

  const isDragHandleTarget = React.useCallback((target: EventTarget | null): boolean => {
    const element = target as HTMLElement | null;
    if (!element || typeof element.closest !== 'function') return false;
    return Boolean(element.closest('[data-drag-handle="true"]'));
  }, []);

  const stopTableDragScroll = React.useCallback(() => {
    if (!tableDragScrollRef.current.active) return;
    tableDragScrollRef.current.active = false;
    setIsTableDragScrolling(false);
  }, []);

  React.useEffect(() => {
    if (!isTableDragScrolling) return undefined;

    const handleMouseMove = (event: MouseEvent) => {
      const state = tableDragScrollRef.current;
      const container = tableContainerRef.current;
      if (!state.active || !container) return;

      const deltaX = event.clientX - state.startX;
      if (Math.abs(deltaX) > 3) {
        state.moved = true;
        suppressTableClickRef.current = true;
      }
      container.scrollLeft = state.scrollLeft - deltaX;
      event.preventDefault();
    };

    const handleMouseUp = () => {
      stopTableDragScroll();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isTableDragScrolling, stopTableDragScroll]);

  useEffect(() => {
    if (productTypesProp === undefined || productTypesProp === null) {
      getProductTypeTax().then(data => setLocalProductTypes(data.filter(pt => pt.is_active === true))).catch(() => {});
    }
  }, [productTypesProp]);

  useEffect(() => {
    const handleResize = () => {
      const finalWidth = calculateTableWidth({
        windowWidth: window.innerWidth,
        sidebarOpen,
      });
      setTableWidth(finalWidth);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        let contentTypeId: number | null = null;
        for (const modelName of ['orderitem', 'order_item']) {
          try {
            contentTypeId = await getContentTypeId(modelName);
            break;
          } catch (_innerError) {
            contentTypeId = null;
          }
        }
        if (!cancelled) {
          setOrderItemContentTypeId(contentTypeId);
        }
      } catch (_error) {
        if (!cancelled) {
          setOrderItemContentTypeId(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const nonGroupItemIds = React.useMemo(
    () => orderItems.filter((entry) => !entry.isGroup).map((entry) => String(entry.id)),
    [orderItems]
  );

  const loadItemDocuments = React.useCallback(async (itemId: number | string, options?: { force?: boolean }) => {
    if (!orderItemContentTypeId) {
      return;
    }

    const itemKey = String(itemId);
    const forceReload = options?.force === true;

    if (!forceReload && loadedDocumentItemIdsRef.current.has(itemKey)) {
      return;
    }

    if (!forceReload) {
      const inFlightRequest = inFlightDocumentsRequestRef.current.get(itemKey);
      if (inFlightRequest) {
        await inFlightRequest;
        return;
      }
    }

    setItemDocumentsLoadingMap((prev) => ({ ...prev, [itemKey]: true }));
    const request = getDocumentsByObject(orderItemContentTypeId, itemKey);
    inFlightDocumentsRequestRef.current.set(itemKey, request);

    try {
      const docs = await request;
      setItemDocumentsMap((prev) => ({ ...prev, [itemKey]: docs }));
      loadedDocumentItemIdsRef.current.add(itemKey);
    } catch (_error) {
      setItemDocumentsMap((prev) => ({ ...prev, [itemKey]: [] }));
      loadedDocumentItemIdsRef.current.add(itemKey);
    } finally {
      inFlightDocumentsRequestRef.current.delete(itemKey);
      setItemDocumentsLoadingMap((prev) => ({ ...prev, [itemKey]: false }));
    }
  }, [orderItemContentTypeId]);

  useEffect(() => {
    if (!orderItemContentTypeId || nonGroupItemIds.length === 0) {
      return;
    }

    void Promise.all(nonGroupItemIds.map((itemId) => loadItemDocuments(itemId)));
  }, [orderItemContentTypeId, nonGroupItemIds, loadItemDocuments]);

  useEffect(() => {
    const currentIds = new Set(nonGroupItemIds);

    loadedDocumentItemIdsRef.current.forEach((itemId) => {
      if (!currentIds.has(itemId)) {
        loadedDocumentItemIdsRef.current.delete(itemId);
        inFlightDocumentsRequestRef.current.delete(itemId);
      }
    });

    setItemDocumentsMap((prev) => {
      const next: Record<string, Document[]> = {};
      Object.keys(prev).forEach((itemId) => {
        if (currentIds.has(itemId)) {
          next[itemId] = prev[itemId];
        }
      });
      return next;
    });

    setItemDocumentsLoadingMap((prev) => {
      const next: Record<string, boolean> = {};
      Object.keys(prev).forEach((itemId) => {
        if (currentIds.has(itemId)) {
          next[itemId] = prev[itemId];
        }
      });
      return next;
    });
  }, [nonGroupItemIds]);

  const handleDeleteItemDocument = React.useCallback(async (itemId: number | string, documentId: string) => {
    await deleteDocument(documentId);
    await loadItemDocuments(itemId, { force: true });
  }, [loadItemDocuments]);

  const productTypes = (productTypesProp && productTypesProp.length > 0) ? productTypesProp : localProductTypes;
  const selectedItem = React.useMemo(
    () => orderItems.find((item) => item.id === selectedItemId) || null,
    [orderItems, selectedItemId]
  );

  // Auto-set default product type for items without item_type_tax
  useEffect(() => {
    const defaultPt = productTypes.find(pt => pt.is_default === true);
    if (!defaultPt || !defaultPt.id) return;
    orderItems.forEach(item => {
      if (!item.isGroup && !item.item_type_tax) {
        onItemChange(item.id, "item_type_tax", defaultPt.id);
        const taxRate = defaultPt.value || 0;
        onItemChange(item.id, "total_price_not_tax", item.total_price - item.total_price * taxRate / 100);
      }
    });
  }, [productTypes, orderItems]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const isDraggingRef = React.useRef(false);

  const handleDragStart = () => {
    isDraggingRef.current = true;
  };

  const [createMode, setCreateMode] = React.useState<'product' | 'group' | null>(null);
  const pendingCreateItemRef = React.useRef<OrderItem | null>(null);

  React.useEffect(() => {
    const pendingItem = pendingCreateItemRef.current;
    if (pendingItem && orderItems.length > 0) {
      const maxId = Math.max(...orderItems.map(item => item.id));
      const fields = ['product_name', 'color', 'quantity', 'unit_id', 'unit_name', 'unit_price', 'total_price', 'total_price_not_tax', 'item_type_tax', 'notes', 'groupName'] as const;
      fields.forEach(field => {
        const value = pendingItem[field];
        if (value !== undefined && value !== '') {
          onItemChange(maxId, field as keyof OrderItem, value);
        }
      });
      if (pendingItem.unit) {
        onItemChange(maxId, 'unit', pendingItem.unit);
      }
      pendingCreateItemRef.current = null;
    }
  }, [orderItems]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id && onReorderItems) {
      const oldIndex = orderItems.findIndex(item => item.id === active.id);
      const newIndex = orderItems.findIndex(item => item.id === over?.id);
      
      const newOrder = arrayMove(orderItems, oldIndex, newIndex);
      onReorderItems(newOrder);
    }

    setTimeout(() => { isDraggingRef.current = false; }, 50);
  };

  return (
    <Box sx={styles.sectionWrapper}>
      <Typography
        variant="h6"
        sx={styles.title}
      >
        Sản phẩm
      </Typography>
      
      <TableContainer
        ref={tableContainerRef}
        onMouseDown={(event: React.MouseEvent<HTMLDivElement>) => {
          if (event.button !== 0) return;
          if (isInteractiveTarget(event.target) || isDragHandleTarget(event.target)) return;

          const container = tableContainerRef.current;
          if (!container || container.scrollWidth <= container.clientWidth) return;

          tableDragScrollRef.current = { active: true, moved: false, startX: event.clientX, scrollLeft: container.scrollLeft };
          suppressTableClickRef.current = false;
          setIsTableDragScrolling(true);
          event.preventDefault();
        }}
        onClickCapture={(event: React.MouseEvent<HTMLDivElement>) => {
          if (!suppressTableClickRef.current) return;
          if (isInteractiveTarget(event.target)) return;
          suppressTableClickRef.current = false;
          event.preventDefault();
          event.stopPropagation();
        }}
        sx={
          {
            ...styles.tableContainer(tableWidth, PAPER_HORIZONTAL_PADDING),
            paddingBottom: 0,
            cursor: isTableDragScrolling ? 'grabbing' : 'grab',
            userSelect: isTableDragScrolling ? 'none' : undefined,
          } as any
        }
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={orderItems.map(item => item.id)}
            strategy={verticalListSortingStrategy}
          >
            <Table size="small" sx={styles.table}>
              <TableHead>
                <TableRow sx={styles.headRow}>
                  <TableCell sx={styles.headIndexCellCombined}>
                    <Box sx={styles.headIndexBox}>#</Box>
                  </TableCell>
                  <TableCell sx={styles.headProductCellCombined}>Tên hàng & quy cách</TableCell>
                  <TableCell sx={styles.headColorCellCombined}>Màu sắc</TableCell>
                  <TableCell sx={styles.headQuantityCellCombined}>SL</TableCell>
                  <TableCell sx={{ ...styles.headUnitCellCombined, width: 90, minWidth: 90, maxWidth: 90 }}>ĐVT</TableCell>
                  <TableCell sx={{ ...styles.headUnitPriceCellCombined, textAlign: 'right' }}>Đơn giá</TableCell>
                  <TableCell sx={{ ...styles.headTotalCellCombined, textAlign: 'right' }}>Thành tiền</TableCell>
                  <TableCell sx={styles.headTaxCellCombined}>Phân loại</TableCell>
                  <TableCell sx={{ ...styles.headVatCellCombined, textAlign: 'right' }}>Thành tiền chưa VAT</TableCell>
                  <TableCell
                    sx={{
                      ...styles.headActionWidthCell,
                      minWidth: 150,
                      width: 150,
                      maxWidth: 150,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#374151', fontWeight: 600 }}>
                        File
                      </Typography>
                      <Tooltip title="Upload file/ảnh cho từng item">
                        <CloudUploadIcon fontSize="small" sx={{ color: '#6b7280', fontSize: 18 }} />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell sx={styles.headActionWidthCell}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orderItems.map((item, index) => (
                  <SortableTableRow
                    key={item.id}
                    item={item}
                    index={index}
                    orderItems={orderItems}
                    productTypes={productTypes}
                    disabled={disabled}
                    orderItemContentTypeId={orderItemContentTypeId}
                    onEditItem={(rowItem) => setSelectedItemId(rowItem.id)}
                    onItemChange={onItemChange}
                    onRemoveOrderItem={(itemId) => {
                      const item = orderItems.find((i) => i.id === itemId);
                      if (item) {
                        setDeleteConfirmItem(item);
                      }
                    }}
                    onUploadFiles={onUploadFiles}
                    isDraggingRef={isDraggingRef}
                    itemDocuments={itemDocumentsMap[String(item.id)] || []}
                    isLoadingItemDocuments={!!itemDocumentsLoadingMap[String(item.id)]}
                    onReloadItemDocuments={loadItemDocuments}
                    onDeleteItemDocument={handleDeleteItemDocument}
                  />
                ))}
              </TableBody>
            </Table>
          </SortableContext>
        </DndContext>
      </TableContainer>
      
      <Box sx={styles.actionBar}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setCreateMode('product')}
          disabled={disabled}
          size="small"
          sx={styles.addButton}
        >
          Thêm sản phẩm
        </Button>

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setCreateMode('group')}
          disabled={disabled}
          size="small"
          sx={styles.addButton}
        >
          Thêm phân vùng
        </Button>
      </Box>

      <OrderItemEditModal
        open={!!selectedItem || !!createMode}
        item={selectedItem || (createMode ? {
          id: 0,
          product_name: '',
          item_type_tax: '',
          color: '',
          quantity: 0,
          unit_id: '',
          unit_name: '',
          unit_price: 0,
          total_price_not_tax: 0,
          total_price: 0,
          isGroup: createMode === 'group',
          groupName: '',
        } : null)}
        units={units}
        productTypes={productTypes}
        disabled={disabled}
        isCreate={!!createMode}
        orderItemContentTypeId={orderItemContentTypeId}
        itemDocuments={selectedItem ? (itemDocumentsMap[String(selectedItem.id)] || []) : []}
        isLoadingItemDocuments={selectedItem ? !!itemDocumentsLoadingMap[String(selectedItem.id)] : false}
        onClose={() => { setSelectedItemId(null); setCreateMode(null); }}
        onItemChange={onItemChange}
        onUploadFiles={onUploadFiles}
        onReloadItemDocuments={loadItemDocuments}
        onDeleteItemDocument={handleDeleteItemDocument}
        onCreateItem={(newItem) => {
          pendingCreateItemRef.current = newItem;
          if (createMode === 'group') {
            onAddGroupItem();
          } else {
            onAddOrderItem();
          }
          setCreateMode(null);
        }}
      />
      <DeleteConfirmationModal
        open={!!deleteConfirmItem}
        onClose={() => setDeleteConfirmItem(null)}
        onConfirm={() => {
          if (deleteConfirmItem) {
            onRemoveOrderItem(deleteConfirmItem.id);
            setDeleteConfirmItem(null);
          }
        }}
        title="Xác nhận xóa"
        message={`Bạn có chắc chắn muốn xóa "${deleteConfirmItem?.product_name || deleteConfirmItem?.groupName || 'sản phẩm'}" không? Tất cả file đính kèm cũng sẽ bị xóa.`}
        itemName="sản phẩm"
      />
    </Box>
  );
}
